import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { decoratorAPI, bookingAPI } from '../utils/api';
import toast from 'react-hot-toast';
import './BookingPage.css';

const EVENTS = ['Shaadi', 'Engagement', 'Birthday', 'Rice Ceremony', 'Griha Pravesh', 'Corporate', 'Baby Shower', 'Anniversary'];

export default function BookingPage() {
  const { decoratorId } = useParams();
  const navigate = useNavigate();
  const [decorator, setDecorator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [advanceRequested, setAdvanceRequested] = useState(false);

  const [form, setForm] = useState({
    eventType: '',
    eventDate: '',
    address: '',
    city: '',
    guestCount: '',
    requirements: '',
    totalAmount: ''
  });

  useEffect(() => {
    decoratorAPI.getOne(decoratorId)
      .then(({ data }) => setDecorator(data.decorator))
      .catch(() => toast.error('Decorator nahi mila'))
      .finally(() => setLoading(false));
  }, [decoratorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.eventType || !form.eventDate || !form.address || !form.city || !form.totalAmount) {
      return toast.error('Sab required fields bharo');
    }

    setSubmitting(true);
    try {
      const { data } = await bookingAPI.create({
        decoratorId,
        eventType: form.eventType,
        eventDate: form.eventDate,
        venue: { address: form.address, city: form.city },
        guestCount: form.guestCount,
        requirements: form.requirements,
        totalAmount: Number(form.totalAmount),
        advanceRequested: advanceRequested,
        advanceAmount: advanceRequested ? Math.round(Number(form.totalAmount) * 0.3) : 0,
      });

      toast.success('Booking request bhej di gayi! Decorator se baat karo. 🎉');
      navigate(`/bookings/${data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Booking nahi hui');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="page"><div className="spinner" /></div>;

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 2);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <div className="booking-page page">
      <div className="container">
        <h1>Booking Karo</h1>

        {/* Decorator Info Strip */}
        {decorator && (
          <div className="decorator-strip card">
            <img src={decorator.portfolio?.[0]?.image || 'https://via.placeholder.com/80'} alt={decorator.businessName} />
            <div>
              <h3>{decorator.businessName}</h3>
              <p>📍 {decorator.city} • ₹{(decorator.priceRange?.min / 1000).toFixed(0)}k - ₹{(decorator.priceRange?.max / 1000).toFixed(0)}k</p>
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="how-it-works card" style={{
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '24px'
        }}>
          <h4 style={{ color: '#166534', marginBottom: '12px' }}>📋 Booking Kaise Hogi?</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { step: '1', text: 'Tum form bharo aur request bhejo', color: '#16a34a' },
              { step: '2', text: 'Decorator request dekhega aur accept/decline karega', color: '#16a34a' },
              { step: '3', text: 'Chat karke details aur amount fix karo', color: '#16a34a' },
              { step: '4', text: 'Dono confirm karo — Booking pakki! 🎉', color: '#16a34a' },
              { step: '5', text: 'Payment seedha decorator ko cash mein karo', color: '#16a34a' },
            ].map(({ step, text, color }) => (
              <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  background: color, color: 'white',
                  borderRadius: '50%', width: '24px', height: '24px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700, flexShrink: 0
                }}>{step}</span>
                <span style={{ fontSize: '14px', color: '#15803d' }}>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Booking Form */}
        <div className="booking-form card">
          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label>Event Type *</label>
                <select className="input" value={form.eventType} onChange={e => setForm({ ...form, eventType: e.target.value })}>
                  <option value="">Event chuno</option>
                  {EVENTS.map(e => <option key={e}>{e}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Event Date *</label>
                <input className="input" type="date" min={minDateStr} value={form.eventDate} onChange={e => setForm({ ...form, eventDate: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Venue Address *</label>
              <input className="input" placeholder="Jaise: Mangal Bhavan, Supela, Bhilai" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>City *</label>
                <input className="input" placeholder="Durg" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
              </div>

              <div className="form-group">
                <label>Expected Guests</label>
                <input className="input" type="number" placeholder="100" value={form.guestCount} onChange={e => setForm({ ...form, guestCount: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Special Requirements</label>
              <textarea className="input" rows={3} placeholder="Koi khaas theme, colors, ya requirements ho toh batao..." value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Approximate Budget (₹) *</label>
              <input className="input" type="number" placeholder="40000" value={form.totalAmount} onChange={e => setForm({ ...form, totalAmount: e.target.value })} />
              {form.totalAmount && (
                <div className="amount-breakdown">
                  <div><span>Total Budget</span><strong>₹{Number(form.totalAmount).toLocaleString()}</strong></div>

                  {/* Advance Optional Toggle */}
                  <div style={{
                    margin: '12px 0',
                    padding: '14px',
                    background: '#f8fafc',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={advanceRequested}
                        onChange={e => setAdvanceRequested(e.target.checked)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <div>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                          Advance dena chahte ho? (Optional)
                        </span>
                        <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                          Advance se decorator ko aapki booking serious lagti hai
                        </p>
                      </div>
                    </label>
                    {advanceRequested && (
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b' }}>Advance Amount (30%)</span>
                          <strong style={{ color: '#f59e0b' }}>₹{(form.totalAmount * 0.3).toFixed(0)}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                          <span style={{ color: '#64748b' }}>Baaki Event Pe</span>
                          <strong>₹{(form.totalAmount * 0.7).toFixed(0)}</strong>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '8px 12px', background: '#ecfdf5', borderRadius: '8px' }}>
                    <span style={{ color: '#10b981', fontWeight: 600, fontSize: '13px' }}>
                      ✅ Platform charge: Rs.0 — Tumhare liye BILKUL FREE!
                    </span>
                  </div>
                  <div style={{ marginTop: '6px', padding: '8px 12px', background: '#fefce8', borderRadius: '8px' }}>
                    <span style={{ color: '#92400e', fontSize: '13px' }}>
                      💵 Payment seedha decorator ko cash mein hogi
                    </span>
                  </div>
                </div>
              )}
            </div>

            <button
              className="btn btn-primary"
              type="submit"
              style={{ width: '100%', justifyContent: 'center', padding: '14px' }}
              disabled={submitting}
            >
              {submitting ? 'Booking ho rahi hai...' : '📩 Booking Request Bhejo'}
            </button>

            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', marginTop: '12px' }}>
              Request bhejne ke baad decorator se chat karke sab confirm karo
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}